import React, { useRef } from 'react';
import { Header as DefaultHeaderForm, Header } from 'new/elements/Form/Header';
import { Form } from 'new/elements/Form';
import { ROUTE_PIPELINES } from 'js/constants/pathnames';
import { FORM_TYPE_CREATE_DIRECT_FLOW } from 'js/constants/formTypes';
import { useDirectFlow } from 'new/components/flows/types/DirectFlow/hooks/useDirectFlow';
import { useStartSink } from 'new/components/flows/types/DirectFlow/hooks/useStartSink';
import { useStartSource } from 'new/components/flows/types/DirectFlow/hooks/useStartSource';
import { AuthenticateStep } from 'new/components/flows/types/DirectFlow/steps/AuthenticateStep';
import { ConfigureStep } from 'new/components/flows/types/DirectFlow/steps/ConfigureStep';
import { ConnectStep } from 'new/components/flows/types/DirectFlow/steps/ConnectStep';
import { ROUTE_CONNECT } from 'new/constants/pathnames';
import { flowDataMap } from 'new/elements/flows/dataMap';
import ContextualSupportContainer from 'js/components/support/ContextualSupportContainer';
import useFlowType from './hooks/useFlowType';
import useFlowParams from 'new/components/flows/useFlowParams';
import FlowParamsProvider from 'new/components/flows/FlowParamsProvider';
import { HeaderMetaConfigProvider } from 'new/components/flows/context/HeaderMetaConfigProvider';
import flowTypes from 'new/components/flows/types/flowTypes';

interface CreateDirectFlowProps {
  id?: number;
  flowType?: keyof typeof flowTypes;
  onDismiss?: (data: any) => void;
  isModal?: boolean;
  isEditMode?: boolean;
  resourceType?: 'source' | 'sink';
}

export const useStart = ({ useStartSink, useStartSource, connectionType }) => {
  const useRootSetup = connectionType === 'SOURCE' ? useStartSource : useStartSink;
  useRootSetup();
};

const CONFIGURE_STEP = 2;

const HeaderComponent = ({ isModal }: { isModal: boolean }) => {
  const params = useFlowParams();
  const { showEditedNexsetView } = useDirectFlow();
  const connectionType = params.resourceType.toUpperCase();
  const flowType = useFlowType();
  const data = flowDataMap[flowType];

  if (connectionType === 'SINK' && showEditedNexsetView)
    return (
      <DefaultHeaderForm
        hideContextualMenu={isModal}
        title="Send Nexset to Destination"
        overrideStepsSection={<ContextualSupportContainer />}
      />
    );

  const type = connectionType === 'SINK' ? 'Destination' : 'Source';

  return (
    <Header
      hideContextualMenu={isModal}
      title={
        <>
          {data.shortTitle} <span style={{ color: 'var(--color-greyscale-200)' }}>/</span>{' '}
          {params?.id ? `Editing Data ${type}` : `New Data ${type}`}
        </>
      }
    />
  );
};

const CreateDirectFlow = (props: CreateDirectFlowProps) => {
  const start = useRef(true);
  const { isModal, onDismiss } = props;
  const flowType = useFlowType();
  const params = useFlowParams();
  const { onStartForm, updateFormData, ...formData } = useDirectFlow();
  const connectionType = params.resourceType.toUpperCase();

  useStart({ useStartSink, useStartSource, connectionType });

  if (start.current) {
    onStartForm();
    updateFormData({ flowType, connectionType });
    start.current = false;
  }

  const onFormDismiss = () => {
    if (isModal) {
      onDismiss?.(formData);
      return true;
    }
    return false;
  };

  return (
    <HeaderMetaConfigProvider>
      <Form
        formType={FORM_TYPE_CREATE_DIRECT_FLOW}
        isModal={isModal}
        isEditMode={!!params.id}
        onDismiss={onFormDismiss}
        startPage={!!params.id ? CONFIGURE_STEP : undefined}
        HeaderComponent={<HeaderComponent isModal={isModal} />}
      >
        <ConnectStep />
        <AuthenticateStep />
        <ConfigureStep />
      </Form>
    </HeaderMetaConfigProvider>
  );
};

const CreateDirectFlowContent = (props: CreateDirectFlowProps) => {
  return (
    <FlowParamsProvider {...props}>
      <CreateDirectFlow {...props} />
    </FlowParamsProvider>
  );
};

export default CreateDirectFlowContent;

export const CreateCustomFlow = (props: CreateDirectFlowProps) => {
  return <CreateDirectFlowContent {...props} />;
};

CreateCustomFlow.screenConfig = {
  domain: 'main',
  path: [
    `${ROUTE_CONNECT}${ROUTE_PIPELINES}/custom/:resourceType/new`,
    `${ROUTE_CONNECT}${ROUTE_PIPELINES}/custom/:resourceType/:id/edit`,
  ],
  // RouterManager keys its route registry by `${domain}.${screenName}` —
  // ByoCreateCanvas.screenConfig (net-new to this branch, not present on
  // develop) also uses domain: 'main' and used to share this exact
  // screenName, so whichever screen was registered later in router.ts's
  // array silently overwrote the other's route registration entirely.
  // screenName is purely the internal registry key (RouterManager.ts:104) —
  // pageTitle/header below are what's actually shown in the UI, unchanged.
  // Confirmed bug, fixed 2026-08-31.
  screenName: 'BYO Flow (Legacy)',
  pageTitle: 'BYO Flow',
  header: {
    title: 'BYO Flow',
    slugTitle: 'BYO Flow',
  },
};

CreateDirectFlowContent.screenConfig = {
  domain: 'main',
  path: [
    `${ROUTE_CONNECT}${ROUTE_PIPELINES}/in_memory/:resourceType/new`,
    `${ROUTE_CONNECT}${ROUTE_PIPELINES}/in_memory/:resourceType/:id/edit`,
  ],
  screenName: 'Direct Flow',
  pageTitle: 'Direct Flow',
  header: {
    title: 'Direct Flow',
    slugTitle: 'Direct Flow',
  },
};
